/**
 * Table-of-contents helpers for the Guidelines reader.
 *
 * `extractHeadings` is the single source of truth for both the TOC list and the
 * DOM heading anchors: it walks the raw markdown (skipping fenced code blocks)
 * and returns headings in document order with stable, unique slug ids. The
 * reader assigns those same ids to the rendered <h1/h2/h3> elements by index,
 * so a TOC click reliably scrolls to the right section.
 */

const slugify = (text) =>
  String(text)
    .toLowerCase()
    .replace(/[`*_~]/g, "") // strip inline markdown emphasis marks
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "section";

/**
 * @param {string} markdown
 * @param {{ maxLevel?: number }} [opts] — deepest heading level to include (default 3)
 * @returns {Array<{ id: string, level: number, text: string }>}
 */
export function extractHeadings(markdown, { maxLevel = 3 } = {}) {
  if (!markdown) return [];
  const lines = String(markdown).split("\n");
  const headings = [];
  const seen = new Map();
  let inFence = false;

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^\s*(```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const m = /^(#{1,6})\s+(.*)$/.exec(line);
    if (!m) continue;
    const level = m[1].length;
    if (level > maxLevel) continue;

    const text = m[2].replace(/\s+#+\s*$/, "").trim(); // drop trailing "###"
    if (!text) continue;

    let id = slugify(text);
    if (seen.has(id)) {
      const n = seen.get(id) + 1;
      seen.set(id, n);
      id = `${id}-${n}`;
    } else {
      seen.set(id, 0);
    }
    headings.push({ id, level, text });
  }
  return headings;
}
